---
title: Respecting reduce motion
---

If you can't tell already, I love ✨ motion ✨. But some people really don't. They could be using a slow device, have a personal preference, or even have a vestibular disorder where motion can literally make them sick. Either way, it's easy to implement. (And necessary, if you want to pass 
[WCAG 2.3.3: Animation from Interactions](https://www.w3.org/WAI/WCAG22/Understanding/animation-from-interactions.html))

<img src="/thoughts/respecting-reduce-motion/optimized/reduce-motion.webp" alt="macOS reduce motion setting" className="rounded-md" />

## motion.dev 

I use motion.dev for a lot of my animations. One of the things I love about it is how easy it is to make accessible animations. 

### 

Setting `reduceMotion="user"` in your Motion config will automatically disable all `transform` and `layout` animations. This gets us most of the way there. 

```jsx
import { MotionConfig } from 'framer-motion'

<MotionConfig reducedMotion="user">
  {/* app content */}
</MotionConfig>
```

### useReducedMotion()

There's also a hook that will return a boolean describing whether or not a user has turned on reduced motion. This can be used to go a little further. I use a lot of blur in my animations to support transitions, so I like to disable that as well. 

```jsx
import { useReducedMotion } from 'framer-motion'

function Component() {
  const shouldReduceMotion = useReducedMotion()

  return (
    <motion.div
      initial={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, filter: 'blur(8px)' }}
      animate={shouldReduceMotion ? { opacity: 1 } : { opacity: 1, filter: 'blur(0px)' }}
      transition={shouldReduceMotion ? { duration: 0 } : { duration: 0.3 }}
    />
  )
}
```

### CSS 

I also use CSS for some animations. There's a media query built into CSS now called `prefers-reduced-motion` that can be used. The following code block will force any transforms off: 
```css
@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}
```
